import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Save } from 'lucide-react';
import { logger } from '@/utils/logger';
import { captureError } from '@/debug/debugStore';
import { useAuthContext } from '@/contexts/useAuthContext';
import { uploadParentPhoto, updateParentProfile } from '@/api/services/profilesApi';
import type { UpdateParentProfileRequest } from '@/types/api';
import { useI18n } from '@/i18n';
import { useNavigate } from '@/i18n/navigation';

const EditParentProfilePage: React.FC = () => {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { firebaseUser, loading, profile, backendUser, updateProfile } = useAuthContext();
  const isZh = locale === 'zh-CN';

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<UpdateParentProfileRequest>({
    fullName: '',
    phone: '',
    nationality: '',
    gender: undefined,
  });

  // Auth guard
  useEffect(() => {
    if (!loading) {
      if (!firebaseUser || profile?.role !== 'parent') {
        navigate('/');
        if (!firebaseUser) {
          toast.error(isZh ? '请先登录以访问此页面' : 'Please log in to access this page');
        } else {
          toast.error(isZh ? '只有家长可以访问此页面' : 'Only parents can access this page');
        }
      }
    }
  }, [firebaseUser, profile, loading, navigate, isZh]);

  // Pre-fill form with existing profile data
  useEffect(() => {
    if (profile && profile.role === 'parent') {
      setFormData({
        fullName: profile.fullName || '',
        phone: profile.phone || '',
        nationality: profile.nationality || '',
        gender: (profile.gender as 'male' | 'female' | 'other' | undefined) ?? undefined,
      });
      // Seed avatar from persisted profile
      const existing = profile.profilePhotoUrl || profile.profilePicture;
      if (existing) setLocalAvatarUrl(existing);
    }
  }, [profile]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setIsUploadingAvatar(true);
    try {
      const result = await uploadParentPhoto(file);
      setLocalAvatarUrl(result.photoUrl);
      if (profile) {
        updateProfile({ ...profile, profilePhotoUrl: result.photoUrl });
      }
      toast.success(isZh ? '头像更新成功！' : 'Profile photo updated!');
    } catch (err) {
      logger.error('EditParentProfilePage', 'Failed to upload photo', err);
      captureError('EditParent:photoUpload', err);
      toast.error(isZh ? '上传头像失败，请再试一次。' : 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName?.trim()) {
      toast.error(isZh ? '姓名为必填项' : 'Full name is required');
      return;
    }
    if (!formData.phone?.trim()) {
      toast.error(isZh ? '电话号码为必填项' : 'Phone number is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const updated = await updateParentProfile(formData);
      if (backendUser) {
        updateProfile({ ...updated, role: 'parent' });
      }
      toast.success(isZh ? '资料更新成功！' : 'Profile updated successfully!');
      navigate(-1);
    } catch (err) {
      logger.error('EditParentProfilePage', 'Failed to save profile', err);
      captureError('EditParent:submit', err);
      toast.error(isZh ? '保存资料失败，请再试一次。' : 'Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayAvatarUrl =
    localAvatarUrl ||
    profile?.profilePhotoUrl ||
    profile?.profilePicture ||
    null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{isZh ? '编辑资料' : 'Edit Profile'}</h1>
          <p className="text-lg text-gray-600">{isZh ? '更新你的家长账户资料' : 'Update your parent account information'}</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Avatar Upload Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 flex items-center gap-6">
            {/* Avatar preview */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 overflow-hidden flex items-center justify-center">
                {isUploadingAvatar ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                ) : displayAvatarUrl ? (
                  <img
                    src={displayAvatarUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-gray-400">
                    {formData.fullName?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <label
                htmlFor="parent-avatar-upload"
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors shadow"
              >
                <Camera className="w-3.5 h-3.5 text-white" />
              </label>
            </div>

            {/* Upload text */}
            <div>
              <p className="font-semibold text-gray-900">{isZh ? '头像' : 'Profile Photo'}</p>
              <p className="text-sm text-gray-500 mt-0.5">{isZh ? 'JPG、PNG 或 WebP · 最大 5 MB' : 'JPG, PNG or WebP · max 5 MB'}</p>
              <label
                htmlFor="parent-avatar-upload"
                className="mt-2 inline-block text-sm text-primary font-medium cursor-pointer hover:underline"
              >
                {isZh ? '更换头像' : 'Change photo'}
              </label>
            </div>

            <input
              id="parent-avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>

          {/* Info Fields */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900">{isZh ? '个人资料' : 'Personal Information'}</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isZh ? '姓名' : 'Full Name'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName || ''}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="input-field w-full"
                placeholder={isZh ? '请输入你的姓名' : 'Enter your full name'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isZh ? '电话号码' : 'Phone Number'} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-field w-full"
                placeholder={isZh ? '例如：+601112049396' : 'e.g. +601112049396'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isZh ? '国籍' : 'Nationality'}
              </label>
              <input
                type="text"
                value={formData.nationality || ''}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                className="input-field w-full"
                placeholder={isZh ? '例如：马来西亚人' : 'e.g. Malaysian'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{isZh ? '性别' : 'Gender'}</label>
              <div className="flex gap-3">
                {(['male', 'female', 'other'] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, gender: formData.gender === g ? undefined : g })
                    }
                    className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${
                      formData.gender === g
                        ? 'border-secondary bg-secondary-50 text-secondary'
                        : 'border-gray-300 text-gray-700 hover:border-secondary'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              {isZh ? '取消' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isZh ? '保存中...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isZh ? '保存更改' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditParentProfilePage;
